import React, {useRef, useLayoutEffect} from "react";
import {observer} from "mobx-react";
import {useNavigate} from "react-router-dom";
import {ModalContent} from "views/components/modals/common";
import {Button, Modal, Stack, Typography} from "@mui/material";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import gameLogo from "assets/img/game_logo.png";
import styled from "@emotion/styled";
import {Checkbox, Label} from "views/components/form";
import {Exist} from "views/components/exist/exist.component";
import DOMPurify from "dompurify";
import {IModalAcceptTermsRegistrationController} from "views/components/modals/modal_accept_terms/modal_accept_terms.controller";
import {AdobeDTM} from "data/utils/AdobeDTM";
import {noop} from "lodash";

const LogoWrapper = styled.div`
	width: 100%;
	height: 80px;
	display: flex;
	justify-content: center;
	align-items: center;
	img {
		height: 100%;
	}
`;

const Form = styled.form`
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	gap: 16px;
	width: 100%;

	button {
		max-width: unset;
	}
`;

const Title = styled.h4`
	color: #ffffff;
	text-align: center;
	font-size: 20px;
	font-style: normal;
	font-weight: 600;
	line-height: 140%; /* 28px */
`;

const Description = styled.p`
	color: #ffffff;
	text-align: center;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
`;

const StyledLabel = styled(Label)`
	a {
		text-decoration: underline;
	}
`;

const Disclaimer = styled(Description)`
	font-size: 12px;

	a {
		text-decoration: underline;
		text-underline-offset: 2px;
	}
`;
export const ModalAcceptTerms: React.FC = observer(() => {
	const navigate = useNavigate();
	const ref = useRef<HTMLDivElement>(null);
	const {i18n, error, isOpen, isDisabled, handleFormSubmit, validationMessage} =
		useViewController<IModalAcceptTermsRegistrationController>(
			Bindings.ModalAcceptTermsRegistrationController,
			{navigate}
		);
	const description = i18n.t("modal.accept_terms.description", "");

	useLayoutEffect(() => {
		const modalContainer = ref.current;
		const eventTrack = (event: MouseEvent) => {
			const checkbox = modalContainer?.querySelector("input");
			const isChecked = checkbox?.checked;
			const target = event.target;
			if (target instanceof Element && target.matches("a, button")) {
				const text = target.textContent || "";
				AdobeDTM.trackAction({
					"action.name": `Spinball: Welcome Modal ${text} Click`,
					"action.category": "Spinball",
					"action.element.text": text,
					...(isChecked ? {"action.options": "Accept Terms and Conditions"} : {}),
				});
			}
		};

		if (modalContainer && isOpen) {
			modalContainer.addEventListener("click", eventTrack);
			return () => modalContainer.removeEventListener("click", eventTrack);
		}

		return noop;
	}, [isOpen]);

	return (
		<Modal open={isOpen}>
			<ModalContent ref={ref}>
				<LogoWrapper>
					<img src={gameLogo} alt="" role={"presentation"} />
				</LogoWrapper>

				<Form onSubmit={handleFormSubmit}>
					<Exist when={Boolean(error)}>
						<Typography variant="h5" color="red">
							{error}
						</Typography>
					</Exist>
					<Title>{i18n.t("modal.accept_terms.title", "Accept Terms")}</Title>
					<Exist when={description.length}>
						<Description>{description}</Description>
					</Exist>
					<Stack
						direction="row"
						justifyContent="center"
						alignItems="center"
						width="100%"
						py={2}
						gap={2}>
						<Checkbox defaultChecked={false} required={true} name="terms" id="terms" />
						<StyledLabel
							htmlFor="terms"
							color="#FFFFFF"
							dangerouslySetInnerHTML={{
								/* eslint-disable-next-line react/no-danger */
								__html: DOMPurify.sanitize(
									i18n.t(
										"modal.accept_terms.check_box_label",
										`Accept <a href="https://mlb.com/apps/spinball/official-rules">Terms and Conditions</a>`
									)
								),
							}}
						/>
					</Stack>
					<Button
						disabled={isDisabled || !!validationMessage}
						type="submit"
						variant="contained"
						color="secondary"
						fullWidth>
						{i18n.t("modal.accept_terms.action", "Continue")}
					</Button>

					<Disclaimer
						dangerouslySetInnerHTML={{
							/* eslint-disable-next-line react/no-danger */
							__html: DOMPurify.sanitize(
								i18n.t(
									"modal.accept_terms.disclaimer",
									`NPN. US and CAN, 18+. Ends 11:59pm ET on 9/28. Restrictions apply; see <a href="https://mlb.com/apps/spinball/official-rules">Official Rules</a>.`
								)
							),
						}}
					/>
				</Form>
			</ModalContent>
		</Modal>
	);
});
